import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck, Inbox } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";

const STATUS_LABELS = {
  confirmed: "Confirmed",
  dispatched: "Dispatched",
  en_route: "En Route",
  chauffeur_arrived: "Arrived",
  passenger_loaded: "In Transit",
  completed: "Completed",
  cancelled: "Cancelled",
};

function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 30) return "Just now";
  if (diff < 60) return `${Math.floor(diff)} s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}

/**
 * Glass dropdown anchored under the nav bell.
 * Closes on outside click, Escape, or row click.
 */
export default function NotificationsDropdown({ onClose, anchorRef }) {
  const { events, unreadCount, markRead, markAllRead } = useNotifications();
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onDoc(e) {
      // Don't close when clicking the anchor (bell) — the bell handles its own toggle
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const handleRowClick = (ev) => {
    if (!ev.read) markRead(ev.id);
    onClose();
    navigate(`/account?b=${ev.booking_id}`);
  };

  return (
    <div
      ref={panelRef}
      data-testid="notifications-dropdown"
      className="absolute top-full right-0 mt-4 w-[22rem] max-w-[calc(100vw-2rem)] backdrop-blur-2xl bg-[#0B0B0C]/95 border border-white/15 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] z-50"
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6]">
            Notifications
          </div>
          <div className="text-sm text-white mt-0.5 font-light">
            {unreadCount > 0
              ? `${unreadCount} unread`
              : "All caught up"}
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            data-testid="notifications-mark-all-read"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] hover:text-white transition-colors"
          >
            <CheckCheck size={12} strokeWidth={1.5} />
            Mark all read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {events.length === 0 ? (
          <div
            data-testid="notifications-empty"
            className="px-5 py-12 text-center"
          >
            <Inbox size={20} strokeWidth={1.25} className="mx-auto text-[#8E9AA6] mb-3" />
            <div className="text-xs text-[#8E9AA6] uppercase tracking-[0.25em]">
              No notifications yet
            </div>
          </div>
        ) : (
          <ul>
            {events.slice(0, 20).map((ev) => (
              <li key={ev.id}>
                <button
                  type="button"
                  onClick={() => handleRowClick(ev)}
                  data-testid={`notification-row-${ev.id}`}
                  className={`w-full text-left px-5 py-4 border-l-2 border-b border-white/5 transition-colors ${
                    ev.read
                      ? "border-l-transparent bg-transparent text-[#8E9AA6] hover:bg-white/[0.03]"
                      : "border-l-[#E6E6E6] bg-white/[0.02] text-white hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <span
                      className={`text-[9px] uppercase tracking-[0.3em] px-2 py-0.5 ${
                        ev.read
                          ? "bg-white/5 text-[#8E9AA6]"
                          : "bg-[#E6E6E6] text-[#0B0B0C]"
                      }`}
                    >
                      {STATUS_LABELS[ev.to_status] || ev.to_status}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#8E9AA6] shrink-0">
                      {relativeTime(ev.created_at)}
                    </span>
                  </div>
                  <p className="text-sm font-light leading-snug">{ev.message}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 py-3 border-t border-white/10 text-center">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
          Tap a notification to view booking
        </span>
      </div>
    </div>
  );
}
